// Portfolio Summary - Main Handler
// Orchestrates data fetching and renders the Portfolio Summary view

import { showLoading, showError, setContent } from '../../shared/ui.ts';
import {
  getAccounts,
  getHoldingsForItem,
  getActivitiesForAccount,
  getStoredVisitorId,
  savePortfolioNavSnapshot,
  type PortfolioNavSnapshot,
} from '../brokerage/plaidApi.ts';
import {
  getOptionQuotes,
  type OptionQuote,
} from '../../api/massiveApi.ts';
import { getHistoricalPrices } from '../../api/workerApi.ts';
import { matchTrades } from '../brokerage/tradeMatching.ts';
import { getSectorData } from './sectorApi.ts';
import { dedupeBrokerageAccountsForAnalysis } from './accountDedupe.ts';
import {
  calculateNetLiq,
  calculateTotalMV,
  calculateAllocationMV,
  calculateUnrealized,
  aggregateGreeks,
  groupBySector,
  addCashExposure,
  groupByGeography,
  groupByMarketCap,
  groupByAssetClass,
  getTopPositions,
  calculateGreekBreakdown,
  calculateDailyPL,
  calculateMetrics,
  calculateRealizedYTD,
  calculateCorrelationMatrix,
} from './summaryCalculator.ts';
import { renderPortfolioAnalysis } from './portfolioAnalysisRenderer.ts';
import type {
  PortfolioAnalysisControls,
  PortfolioPnlRange,
  PortfolioPnlUnit,
  PortfolioAnalysisRenderState,
  PortfolioSummary,
  EquityPoint,
  OptionSnapshotWithGreeks,
  PortfolioAccountOption,
} from './summary.types.ts';
import type { AccountHolding, AccountActivity, BrokerageAccount, TradeMatchingResult } from '../brokerage/brokerage.types.ts';
import type { HistoricalClose } from '../../types.ts';

const SUMMARY_RESULT_ID = 'summary-result';
const SUMMARY_HOLDINGS_CACHE_KEY = 'dyr_summary_holdings_cache_v1';

// State for expanded sections
const expandedSections = new Set<string>(['greeks', 'positions']);
const analysisControls: PortfolioAnalysisControls = {
  pnlUnit: 'dollar',
  pnlRange: 'YTD',
  density: 'compact',
};

let currentSummary: PortfolioSummary | null = null;
let currentRenderState: PortfolioAnalysisRenderState = {};
let summaryLoadPromise: Promise<void> | null = null;

class AnalysisPerfLogger {
  private readonly startedAt = nowMs();
  private lastMark = this.startedAt;
  private open = false;

  constructor(visitorId: string | null) {
    if (typeof console.groupCollapsed === 'function') {
      console.groupCollapsed('[DYR analysis perf] loadSummary');
      this.open = true;
    }
    this.mark('start', { visitorId: visitorId ? 'present' : 'missing' });
  }

  async time<T>(
    label: string,
    task: () => Promise<T>,
    details?: Record<string, unknown>
  ): Promise<T> {
    const startedAt = nowMs();
    console.info(`[DYR analysis perf] ${label} start ${formatPerfDetails({
      elapsedMs: roundMs(startedAt - this.startedAt),
      ...details,
    })}`);

    try {
      const result = await task();
      console.info(`[DYR analysis perf] ${label} done ${formatPerfDetails({
        durationMs: roundMs(nowMs() - startedAt),
        elapsedMs: roundMs(nowMs() - this.startedAt),
      })}`);
      return result;
    } catch (error) {
      console.warn(`[DYR analysis perf] ${label} failed ${formatPerfDetails({
        durationMs: roundMs(nowMs() - startedAt),
        elapsedMs: roundMs(nowMs() - this.startedAt),
        error: error instanceof Error ? error.message : String(error),
      })}`);
      throw error;
    }
  }

  mark(label: string, details?: Record<string, unknown>): void {
    const current = nowMs();
    console.info(`[DYR analysis perf] ${label} ${formatPerfDetails({
      elapsedMs: roundMs(current - this.startedAt),
      sinceLastMs: roundMs(current - this.lastMark),
      ...details,
    })}`);
    this.lastMark = current;
  }

  finish(status: 'complete' | 'empty' | 'error', details?: Record<string, unknown>): void {
    this.mark(`finish:${status}`, details);
    if (this.open) {
      console.groupEnd();
      this.open = false;
    }
  }
}

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function roundMs(ms: number): number {
  return Math.round(ms * 10) / 10;
}

function formatPerfDetails(details: Record<string, unknown>): string {
  try {
    return JSON.stringify(details);
  } catch {
    return '{}';
  }
}

/**
 * Initialize the Summary tab
 */
export function initializeSummary(): void {
  // Add event listeners for collapsible sections
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const unpinControl = target.closest('[data-pnl-unpin]');
    if (unpinControl) {
      unpinPortfolioPnlPoint(unpinControl);
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (target.closest('.pa-chart-tooltip')) {
      return;
    }

    const pnlPoint = target.closest<HTMLElement>('[data-pnl-point]');
    if (pnlPoint) {
      pinPortfolioPnlPoint(pnlPoint);
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const sectionHeader = target.closest('[data-section-toggle]');
    if (sectionHeader) {
      const sectionId = sectionHeader.getAttribute('data-section-toggle');
      if (sectionId) {
        toggleSection(sectionId);
      }
    }
  });

  document.addEventListener('dyr:tab-changed', (event) => {
    const tabId = event instanceof CustomEvent ? event.detail?.tabId : null;
    if (tabId === 'summary') {
      void loadSummary();
    }
  });

  document.addEventListener('dyr:brokerage-accounts-changed', () => {
    if (!document.getElementById('tab-summary')?.classList.contains('hidden')) {
      void loadSummary();
    }
  });

  if (document.getElementById(SUMMARY_RESULT_ID)) {
    void loadSummary();
  }
}

function pinPortfolioPnlPoint(point: HTMLElement): void {
  const chart = point.closest<HTMLElement>('.pa-chart');
  if (!chart) return;

  chart.classList.add('is-pinned');
  chart.querySelectorAll('.pa-chart-hit.pinned').forEach(element => {
    element.classList.remove('pinned');
    element.setAttribute('aria-pressed', 'false');
  });
  point.classList.add('pinned');
  point.setAttribute('aria-pressed', 'true');
  point.focus({ preventScroll: true });
}

function unpinPortfolioPnlPoint(control: Element): void {
  const chart = control.closest<HTMLElement>('.pa-chart');
  if (!chart) return;

  chart.classList.remove('is-pinned');
  chart.querySelectorAll('.pa-chart-hit.pinned').forEach(element => {
    element.classList.remove('pinned');
    element.setAttribute('aria-pressed', 'false');
  });
}

/**
 * Toggle a collapsible section
 */
export function toggleSection(sectionId: string): void {
  if (expandedSections.has(sectionId)) {
    expandedSections.delete(sectionId);
  } else {
    expandedSections.add(sectionId);
  }

  // Update UI
  const content = document.getElementById(`section-${sectionId}`);
  const toggle = document.querySelector(`[data-section-toggle="${sectionId}"]`);

  if (content) {
    content.classList.toggle('hidden', !expandedSections.has(sectionId));
  }
  if (toggle) {
    const arrow = toggle.querySelector('.section-arrow');
    if (arrow) {
      arrow.textContent = expandedSections.has(sectionId) ? '▾' : '▸';
    }
  }
}

export function setPortfolioPnlUnit(unit: PortfolioPnlUnit): void {
  analysisControls.pnlUnit = unit;
  renderCurrentSummary();
}

export function setPortfolioPnlRange(range: PortfolioPnlRange): void {
  analysisControls.pnlRange = range;
  renderCurrentSummary();
}

function renderCurrentSummary(): void {
  if (currentSummary) {
    renderSummary(currentSummary, currentRenderState);
  }
}

/**
 * Load and display the Portfolio Summary
 */
export async function loadSummary(): Promise<void> {
  if (summaryLoadPromise) {
    return summaryLoadPromise;
  }

  summaryLoadPromise = loadSummaryImpl().finally(() => {
    summaryLoadPromise = null;
  });
  return summaryLoadPromise;
}

async function loadSummaryImpl(): Promise<void> {
  const visitorId = getStoredVisitorId();
  const perf = new AnalysisPerfLogger(visitorId);

  if (!visitorId) {
    setContent(SUMMARY_RESULT_ID, `
      <div class="dyr-empty-state">
        <h1>Portfolio Analysis</h1>
        <p>No brokerage account connected.</p>
        <button type="button" onclick="connectBrokerage()">Connect account</button>
      </div>
    `);
    perf.finish('empty', { reason: 'missing visitor id' });
    return;
  }

  showLoading(SUMMARY_RESULT_ID);

  try {
    // 1. Get all accounts
    const rawAccounts = await perf.time('accounts', () => getAccounts(visitorId));
    const dedupeResult = dedupeBrokerageAccountsForAnalysis(rawAccounts, new Set());
    const accounts = dedupeResult.accounts;
    perf.mark('accounts deduped', {
      rawAccounts: rawAccounts.length,
      accounts: accounts.length,
      duplicateGroups: dedupeResult.duplicateGroups.length,
    });

    if (dedupeResult.duplicateGroups.length > 0) {
      console.info('[DYR analysis debug] Deduplicated brokerage accounts', {
        rawAccounts: rawAccounts.length,
        dedupedAccounts: accounts.length,
        duplicateGroups: dedupeResult.duplicateGroups,
      });
    }
    if (accounts.length === 0) {
      setContent(SUMMARY_RESULT_ID, `
        <div class="dyr-empty-state">
          <h1>Portfolio Analysis</h1>
          <p>No accounts found for this visitor ID.</p>
          <button type="button" onclick="connectBrokerage()">Connect account</button>
        </div>
      `);
      perf.finish('empty', { reason: 'no accounts' });
      return;
    }

    let accountOptions = buildAccountOptions(accounts);
    const selectedAccounts = accounts;

    // 2. Get holdings for selected accounts
    let allHoldings: AccountHolding[] = [];
    let totalCash = 0;

    const accountsByItem = groupAccountsByItem(selectedAccounts);
    const itemAccountGroups = [...accountsByItem.values()];
    const holdingsByItem = await Promise.all(itemAccountGroups.map((itemAccounts, index) =>
      perf.time(
        `holdings item ${index + 1}`,
        () => getHoldingsForSummaryItem(visitorId, itemAccounts),
        { accountCount: itemAccounts.length }
      )
    ));

    for (const [index, itemHoldings] of holdingsByItem.entries()) {
      const itemAccounts = itemAccountGroups[index];
      for (const account of itemAccounts) {
        const accountHoldings = itemHoldings.filter(holding => holding.accountId === account.id);
        allHoldings = allHoldings.concat(accountHoldings);
        totalCash += calculateAccountCash(account, accountHoldings);
      }
    }
    perf.mark('holdings aggregated', {
      selectedAccounts: selectedAccounts.length,
      itemGroups: itemAccountGroups.length,
      holdings: allHoldings.length,
      cash: totalCash,
    });
    const topSymbols = getTopSymbolsForAnalytics(allHoldings, 10);
    const navSnapshot = buildPortfolioNavSnapshot(allHoldings, totalCash, selectedAccounts);
    const localSnapshotCurve = portfolioSnapshotsToEquityCurve([], navSnapshot);
    const snapshotCurvePromise = perf.time(
      'portfolio snapshots',
      () => saveAndReadPortfolioSnapshotCurve(visitorId, navSnapshot),
      { selectedAccounts: selectedAccounts.length }
    );
    const fastTradeResults = matchTrades([], undefined, { currentHoldings: allHoldings });
    const initialSummary = calculateSummary(
      allHoldings,
      totalCash,
      new Map<string, OptionSnapshotWithGreeks>(),
      new Map(),
      fastTradeResults,
      {},
      topSymbols,
      accountOptions,
      { equityCurve: localSnapshotCurve }
    );
    renderSummary(initialSummary, {
      currentAnalyticsLoading: true,
      currentAnalyticsMessage: 'Fetching option Greeks and exposure metadata',
      correlationLoading: true,
      correlationMessage: 'Fetching historical closes for correlation matrix',
    });
    perf.mark('current holdings render complete', {
      positions: initialSummary.positions,
      holdings: allHoldings.length,
    });

    // 3. Get option snapshots with Greeks
    const optionSymbols = [...new Set(allHoldings
      .filter(h => h.isOption && h.optionSymbol)
      .map(h => h.optionSymbol!))];

    // 4. Get sector data for all symbols. Quotes and sector lookup can run together.
    const allSymbols = allHoldings
      .filter(h => !h.isCash)
      .map(h => h.underlyingSymbol || h.symbol);
    const uniqueSymbols = [...new Set(allSymbols)];
    const [quotes, sectorData] = await Promise.all([
      optionSymbols.length > 0
        ? perf.time('option quotes', () => getOptionQuotes(optionSymbols), { optionSymbols: optionSymbols.length })
        : Promise.resolve(new Map<string, OptionQuote>()),
      uniqueSymbols.length > 0
        ? perf.time('sector data', () => getSectorData(uniqueSymbols), { symbols: uniqueSymbols.length })
        : Promise.resolve(new Map()),
    ]);
    const optionSnapshots = convertToSnapshots(quotes);
    const snapshotCurve = await snapshotCurvePromise;

    const fastSummary = calculateSummary(
      allHoldings,
      totalCash,
      optionSnapshots,
      sectorData,
      fastTradeResults,
      {},
      topSymbols,
      accountOptions,
      { equityCurve: snapshotCurve }
    );
    renderSummary(fastSummary, {
      correlationLoading: true,
      correlationMessage: 'Fetching historical closes for correlation matrix',
    });
    perf.mark('fast render complete', {
      positions: fastSummary.positions,
      optionSymbols: optionSymbols.length,
      sectorSymbols: uniqueSymbols.length,
    });

    // 5. Get activities for P&L reconstruction (last 365 days)
    let allActivities: AccountActivity[] = [];
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const activityDateRange = {
      startDate: oneYearAgo.toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
    };

    const activityResults = await Promise.all(selectedAccounts.map((account, index) =>
      perf.time(
        `activities account ${index + 1}`,
        async () => {
          try {
            return await getActivitiesForAccount(visitorId, account, activityDateRange);
          } catch (err) {
            console.warn(`Failed to get activities for account ${account.id}:`, err);
            return [] as AccountActivity[];
          }
        },
        { accountIndex: index + 1 }
      )
    ));

    allActivities = activityResults.flat();
    accountOptions = buildAccountOptions(accounts);
    perf.mark('activities aggregated', {
      investmentActivities: activityResults.reduce((sum, activities) => sum + activities.length, 0),
      cashActivities: 0,
      totalActivities: allActivities.length,
      cashTransactionItems: 0,
    });

    // 6. Calculate trade matching for realized P&L
    const tradeResults = matchTrades(allActivities, undefined, { currentHoldings: allHoldings });
    perf.mark('trade matching complete', {
      openLots: tradeResults.openLots.length,
      closedTrades: tradeResults.closedTrades.length,
    });

    // 7. Get historical closes for correlation, exact held-position valuation, and SPY benchmark overlay.
    const historicalDisplaySymbols = normalizeUniqueSymbols(topSymbols);
    const historicalSymbolMap = buildHistoricalSymbolMap(historicalDisplaySymbols);
    const historicalSymbols = [...new Set([...historicalSymbolMap.values()])];
    let historicalPrices: Record<string, HistoricalClose[]> = {};
    try {
      perf.mark('historical plan', {
        displaySymbols: historicalDisplaySymbols.length,
        historicalSymbols: historicalSymbols.length,
      });
      const rawHistoricalPrices = historicalSymbols.length > 0
        ? await perf.time('historical prices', () => getHistoricalPrices(historicalSymbols, 365), { symbols: historicalSymbols.length })
        : {} as Record<string, HistoricalClose[]>;
      historicalPrices = {
        ...mapHistoricalPricesToDisplaySymbols(historicalDisplaySymbols, historicalSymbolMap, rawHistoricalPrices),
      };
      logHistoricalPriceCoverage(historicalPrices);
      perf.mark('historical prices assembled', {
        historicalPriceSymbols: Object.keys(historicalPrices).length,
      });
    } catch (err) {
      console.warn('Failed to get historical prices for portfolio analytics:', err);
    }

    // 8. Calculate all summary metrics
    const summary = calculateSummary(
      allHoldings,
      totalCash,
      optionSnapshots,
      sectorData,
      tradeResults,
      historicalPrices,
      topSymbols,
      accountOptions,
      { equityCurve: snapshotCurve }
    );

    // 9. Render the summary
    renderSummary(summary);
    perf.finish('complete', {
      positions: summary.positions,
      activities: allActivities.length,
      equityCurvePoints: summary.equityCurve.length,
      benchmarkPoints: summary.benchmarkCurve.length,
    });
  } catch (error) {
    console.error('Failed to load summary:', error);
    perf.finish('error', {
      message: error instanceof Error ? error.message : 'Unknown error',
    });
    showError(SUMMARY_RESULT_ID, error instanceof Error ? error.message : 'Failed to load portfolio summary');
  }
}

async function getHoldingsForSummaryItem(
  visitorId: string,
  accounts: BrokerageAccount[]
): Promise<AccountHolding[]> {
  const itemId = accounts[0]?.brokerageAuthorizationId;
  if (!itemId) return [];

  try {
    const holdings = await getHoldingsForItem(visitorId, itemId);
    for (const account of accounts) {
      saveCachedAccountHoldings(
        account.id,
        holdings.filter(holding => holding.accountId === account.id)
      );
    }
    return holdings;
  } catch (err) {
    const cached = accounts.flatMap(account => getCachedAccountHoldings(account.id));
    if (cached.length > 0) {
      console.warn(`Failed to get holdings for item ${itemId}; using cached holdings snapshot:`, err);
      return cached;
    }

    console.warn(`Failed to get holdings for item ${itemId}:`, err);
    return [];
  }
}

function groupAccountsByItem(accounts: BrokerageAccount[]): Map<string, BrokerageAccount[]> {
  const groups = new Map<string, BrokerageAccount[]>();

  for (const account of accounts) {
    const existing = groups.get(account.brokerageAuthorizationId) || [];
    existing.push(account);
    groups.set(account.brokerageAuthorizationId, existing);
  }

  return groups;
}

function calculateAccountCash(
  account: BrokerageAccount,
  holdings: AccountHolding[]
): number {
  const cashHoldings = holdings.filter(h => h.isCash);
  if (cashHoldings.length > 0) {
    return cashHoldings.reduce((sum, holding) => sum + (holding.marketValue || 0), 0);
  }

  if (account.balance != null) {
    // Plaid investment account current balance is total account value, so infer cash from it.
    const nonCashValue = holdings.reduce((sum, holding) => sum + (holding.marketValue || 0), 0);
    return Math.max(0, account.balance - nonCashValue);
  }

  return 0;
}

function buildPortfolioNavSnapshot(
  holdings: AccountHolding[],
  cash: number,
  selectedAccounts: BrokerageAccount[]
): Omit<PortfolioNavSnapshot, 'source'> {
  const date = new Date().toISOString().split('T')[0];
  const selectedAccountIds = selectedAccounts.map(account => account.id).sort();
  const nonCashHoldings = holdings
    .filter(holding => !holding.isCash)
    .map(holding => ({
      symbol: normalizeSnapshotSymbol(holding.optionSymbol || holding.symbol),
      name: holding.description || holding.optionSymbol || holding.symbol,
      type: holding.isOption ? 'option' as const : 'equity' as const,
      quantity: holding.units,
      value: holding.marketValue || 0,
      markSource: 'broker_current' as const,
      markConfidence: 'high' as const,
      markDate: date,
    }))
    .filter(holding => holding.symbol && Number.isFinite(holding.quantity) && Number.isFinite(holding.value));

  const cashHolding = Math.abs(cash) > 0.005
    ? [{
      symbol: 'CASH',
      name: 'Cash',
      type: 'cash' as const,
      quantity: 1,
      value: cash,
      markSource: 'broker_current' as const,
      markConfidence: 'high' as const,
      markDate: date,
    }]
    : [];

  return {
    date,
    capturedAt: new Date().toISOString(),
    selectionKey: 'all',
    selectedAccountIds,
    value: calculateNetLiq(holdings, cash),
    cash,
    totalMV: calculateTotalMV(holdings),
    holdings: [...nonCashHoldings, ...cashHolding],
  };
}

async function saveAndReadPortfolioSnapshotCurve(
  visitorId: string,
  snapshot: Omit<PortfolioNavSnapshot, 'source'>
): Promise<EquityPoint[]> {
  try {
    const snapshots = await savePortfolioNavSnapshot(visitorId, snapshot);
    return portfolioSnapshotsToEquityCurve(snapshots, snapshot);
  } catch (error) {
    console.warn('Failed to store portfolio NAV snapshot; using current snapshot only:', error);
    return portfolioSnapshotsToEquityCurve([], snapshot);
  }
}

function portfolioSnapshotsToEquityCurve(
  snapshots: PortfolioNavSnapshot[],
  fallbackSnapshot: Omit<PortfolioNavSnapshot, 'source'>
): EquityPoint[] {
  const points = snapshots.length > 0 ? snapshots : [{
    ...fallbackSnapshot,
    source: 'broker_snapshot' as const,
  }];

  return points
    .filter(snapshot => Number.isFinite(snapshot.value) && /^\d{4}-\d{2}-\d{2}$/.test(snapshot.date))
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(snapshot => ({
      date: snapshot.date,
      value: snapshot.value,
      holdings: snapshot.holdings,
    }));
}

function normalizeSnapshotSymbol(symbol: string | null | undefined): string {
  return (symbol || 'UNKNOWN').trim().replace(/\s+/g, '').toUpperCase();
}

function getCachedAccountHoldings(accountId: string): AccountHolding[] {
  const cache = readHoldingsCache();
  return cache[accountId]?.holdings || [];
}

function saveCachedAccountHoldings(accountId: string, holdings: AccountHolding[]): void {
  const cache = readHoldingsCache();
  cache[accountId] = {
    cachedAt: new Date().toISOString(),
    holdings,
  };
  sessionStorage.setItem(SUMMARY_HOLDINGS_CACHE_KEY, JSON.stringify(cache));
}

function readHoldingsCache(): Record<string, { cachedAt: string; holdings: AccountHolding[] }> {
  const raw = sessionStorage.getItem(SUMMARY_HOLDINGS_CACHE_KEY);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function buildAccountOptions(accounts: BrokerageAccount[]): PortfolioAccountOption[] {
  return accounts.map(account => ({
    id: account.id,
    itemId: account.brokerageAuthorizationId,
    name: account.name,
    institutionName: account.institutionName,
    mask: account.number || '',
    selected: true,
    balance: account.balance,
    currency: account.currency,
  }));
}

function getTopSymbolsForAnalytics(holdings: AccountHolding[], limit: number): string[] {
  const marketValueBySymbol = new Map<string, number>();

  for (const holding of holdings) {
    if (holding.isCash) continue;
    const symbol = (holding.underlyingSymbol || holding.symbol || '').toUpperCase();
    if (!symbol) continue;
    marketValueBySymbol.set(symbol, (marketValueBySymbol.get(symbol) || 0) + (holding.marketValue || 0));
  }

  return [...marketValueBySymbol.entries()]
    .map(([symbol, marketValue]) => ({ symbol, marketValue: Math.abs(marketValue) }))
    .filter(item => item.marketValue > 0)
    .sort((a, b) => b.marketValue - a.marketValue)
    .map(item => item.symbol)
    .slice(0, limit);
}

function normalizeUniqueSymbols(symbols: string[]): string[] {
  return symbols
    .map(symbol => symbol.toUpperCase().trim())
    .filter(symbol => symbol.length > 0 && !symbol.startsWith('CASH:'))
    .filter((symbol, index, all) => all.indexOf(symbol) === index);
}

function buildHistoricalSymbolMap(symbols: string[]): Map<string, string> {
  return new Map(symbols.map(symbol => [symbol, getHistoricalProxySymbol(symbol)]));
}

function mapHistoricalPricesToDisplaySymbols(
  displaySymbols: string[],
  symbolMap: Map<string, string>,
  historicalPrices: Record<string, HistoricalClose[]>
): Record<string, HistoricalClose[]> {
  const mapped: Record<string, HistoricalClose[]> = {};

  for (const displaySymbol of displaySymbols) {
    const historicalSymbol = symbolMap.get(displaySymbol) || displaySymbol;
    mapped[displaySymbol] = historicalPrices[historicalSymbol] || [];
  }

  return mapped;
}

function logHistoricalPriceCoverage(historicalPrices: Record<string, HistoricalClose[]>): void {
  const rows = Object.entries(historicalPrices)
    .map(([symbol, history]) => {
      const sorted = [...history].sort((a, b) => b.date.localeCompare(a.date));
      return {
        symbol,
        rows: sorted.length,
        newest: sorted[0]?.date || null,
        oldest: sorted[sorted.length - 1]?.date || null,
        newestClose: sorted[0]?.close || null,
        oldestClose: sorted[sorted.length - 1]?.close || null,
      };
    })
    .sort((a, b) => a.symbol.localeCompare(b.symbol));

  console.groupCollapsed('[DYR equity debug] Historical price coverage');
  console.log('[DYR equity debug JSON] historical coverage', JSON.stringify(rows));
  console.table(rows);
  console.groupEnd();
}

function getHistoricalProxySymbol(symbol: string): string {
  const normalized = symbol.toUpperCase().replace(/^\./, '');
  if (normalized === 'SPX' || normalized === 'SPXW') return 'SPY';
  if (normalized === 'NDX') return 'QQQ';
  if (normalized === 'RUT') return 'IWM';
  return normalized;
}

function convertToSnapshots(quotes: Map<string, OptionQuote>): Map<string, OptionSnapshotWithGreeks> {
  const result = new Map<string, OptionSnapshotWithGreeks>();

  for (const [symbol, quote] of quotes) {
    result.set(symbol, {
      occSymbol: quote.occSymbol,
      underlying: quote.underlying,
      strike: quote.strike,
      expiration: quote.expiration,
      type: quote.type,
      fmv: quote.fmv,
      close: quote.close,
      underlyingPrice: quote.underlyingPrice,
      greeks: quote.greeks ? {
        delta: quote.greeks.delta,
        gamma: quote.greeks.gamma,
        theta: quote.greeks.theta,
        vega: quote.greeks.vega,
        iv: quote.iv ?? undefined,
      } : null,
    });
  }

  return result;
}

function calculateSummary(
  holdings: AccountHolding[],
  cash: number,
  snapshots: Map<string, OptionSnapshotWithGreeks>,
  sectorData: Map<string, import('./summary.types.ts').SectorData>,
  tradeResults: TradeMatchingResult,
  historicalPrices: Record<string, HistoricalClose[]>,
  topAnalyticsSymbols: string[],
  accounts: PortfolioAccountOption[],
  options: { equityCurve: EquityPoint[] }
): PortfolioSummary {
  const netLiq = calculateNetLiq(holdings, cash);
  const totalMV = calculateTotalMV(holdings);
  const allocationMV = calculateAllocationMV(holdings);
  const exposureDenominator = allocationMV + Math.max(0, cash);
  const unrealized = calculateUnrealized(holdings);
  const greeks = aggregateGreeks(holdings, snapshots, 'portfolio total');
  const sectors = addCashExposure(groupBySector(holdings, sectorData, exposureDenominator), cash, exposureDenominator);
  const geographies = addCashExposure(groupByGeography(holdings, sectorData, exposureDenominator), cash, exposureDenominator);
  const marketCaps = addCashExposure(groupByMarketCap(holdings, sectorData, exposureDenominator), cash, exposureDenominator);
  const assetClasses = groupByAssetClass(holdings, cash);
  const topPositions = getTopPositions(holdings, sectorData, snapshots, netLiq, 10);
  const topConcentrationPct = topPositions.reduce((sum, position) => sum + position.pct, 0);
  const greekBreakdown = calculateGreekBreakdown(holdings, snapshots);
  const correlationMatrix = calculateCorrelationMatrix(topAnalyticsSymbols, historicalPrices, 90);
  const equityCurve = options.equityCurve;
  const priceCoverage = emptyPriceCoverage(equityCurve.length);
  const externalCashFlows: PortfolioSummary['externalCashFlows'] = [];
  const equityCurveIncludingCashFlows = equityCurve;
  const benchmarkCurve: EquityPoint[] = [];
  const benchmarkCurveIncludingCashFlows: EquityPoint[] = [];
  const dailyPL = calculateDailyPL(equityCurve);
  const metrics = calculateMetrics(equityCurve, tradeResults);
  metrics.beta = null;
  const realizedYTD = calculateRealizedYTD(tradeResults);
  const grossExposure = holdings
    .filter(h => !h.isCash)
    .reduce((sum, h) => sum + Math.abs(h.marketValue || 0), 0);
  const netExposure = holdings
    .filter(h => !h.isCash)
    .reduce((sum, h) => sum + (h.marketValue || 0), 0);

  // Calculate day P&L (last entry in dailyPL)
  const lastDailyPL = dailyPL.length > 0 ? dailyPL[dailyPL.length - 1] : { pnl: 0 };
  const dayPL = lastDailyPL.pnl;
  const prevValue = netLiq - dayPL;
  const dayPLPct = prevValue > 0 ? (dayPL / prevValue) * 100 : 0;

  return {
    netLiq,
    totalMV,
    cash,
    dayPL,
    dayPLPct,
    unrealized: unrealized.pnl,
    unrealizedPct: unrealized.pct,
    realizedYTD,
    positions: holdings.filter(h => !h.isCash).length,
    accounts,
    greeks,
    greekBreakdown,
    sectors,
    geographies,
    marketCaps,
    assetClasses,
    topPositions,
    topConcentrationPct,
    correlationMatrix,
    equityCurve,
    equityCurveIncludingCashFlows,
    externalCashFlows,
    benchmarkCurve,
    benchmarkCurveIncludingCashFlows,
    dailyPL,
    metrics,
    priceCoverage,
    grossExposurePct: netLiq > 0 ? (grossExposure / netLiq) * 100 : 0,
    netExposurePct: netLiq > 0 ? (netExposure / netLiq) * 100 : 0,
  };
}

function emptyPriceCoverage(renderedDates: number) {
  return {
    candidateDates: renderedDates,
    renderedDates,
    skippedDates: 0,
    skippedOptionMarkDates: 0,
    totalOptionMarks: 0,
    lowConfidenceOptionMarks: 0,
    fallbackOptionMarks: 0,
    sourceCounts: {},
    skippedSymbols: [],
    skippedRequiredSymbols: [],
  };
}

function renderSummary(summary: PortfolioSummary, state: PortfolioAnalysisRenderState = {}): void {
  currentSummary = summary;
  currentRenderState = state;
  setContent(SUMMARY_RESULT_ID, renderPortfolioAnalysis(summary, analysisControls, state));
}
